export type LegacyChunkUploadIntent = 'periodic' | 'flush' | 'final';

type SessionUpload = {
  active: Promise<void>;
  flush?: Promise<void>;
  final?: Promise<void>;
};

const uploadsBySession = new Map<string, SessionUpload>();

function clearWhenSettled(sessionId: string, promise: Promise<void>): void {
  void promise.then(
    () => {
      if (uploadsBySession.get(sessionId)?.active === promise) {
        uploadsBySession.delete(sessionId);
      }
    },
    () => {
      if (uploadsBySession.get(sessionId)?.active === promise) {
        uploadsBySession.delete(sessionId);
      }
    }
  );
}

/**
 * Serializes the legacy frontend-owned extraction + upload path per session.
 *
 * Periodic ticks coalesce while work is active. Flush and final requests wait
 * for active work (even when it failed) and each run exactly once. Once a
 * final request is pending, no non-terminal work can be scheduled after it.
 */
export function coordinateLegacyChunkUpload({
  sessionId,
  intent,
  run,
}: {
  sessionId: string;
  intent: LegacyChunkUploadIntent;
  run: () => Promise<void>;
}): Promise<void> {
  const current = uploadsBySession.get(sessionId);

  if (current?.final) {
    return current.final;
  }

  if (intent === 'periodic' && current) {
    return current.active;
  }

  if (intent === 'flush' && current?.flush) {
    return current.flush;
  }

  const previous = current?.active;
  const promise = previous
    ? previous.catch(() => undefined).then(run)
    : Promise.resolve().then(run);
  const next = {
    active: promise,
    ...(intent === 'flush' ? { flush: promise } : {}),
    ...(intent === 'final' ? { final: promise } : {}),
  };
  uploadsBySession.set(sessionId, next);
  clearWhenSettled(sessionId, promise);
  return promise;
}
